import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { DNABox, DNAButton, DNADivider, DNAText, Iffy } from '@alucio/lux-ui';
import DNAPopover from '../../DNA/Popover/DNAPopover';
import DNACommonConfirmation from 'src/components/DNA/Modal/DNACommonConfirmation';
import { styles } from '../AddMeetingFooter';
import { useCustomFormRecordProvider } from 'src/state/context/CustomFormProvider/CustomFormProvider';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { CustomFormRecordORM } from 'src/types/orms';
import { useUserTenant } from 'src/state/redux/selector/user';
import { useAppSettings } from 'src/state/context/AppSettings';
import { GLOBAL_TENANT_FORM_ERROR } from '../../TenantForm/TenantFormProvider';
import { CrmIntegrationType } from '@alucio/aws-beacon-amplify/src/models';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { CRMConnectionStatus } from 'src/state/machines/CRM/crmMachineTypes';

export const useCancelCustomForm = (onConfirmCancel: () => void) => {
  const dispatch = useDispatch();
  const { isDirty } = useCustomFormRecordProvider();

  const onCancel = useCallback(() => {
    if (isDirty) {
      dispatch(
        DNAModalActions.setModal({
          isVisible: true,
          allowBackdropCancel: true,
          component: () => (<DNACommonConfirmation
            cancelText="Continue editing"
            confirmActionText="Discard changes"
            onConfirmAction={onConfirmCancel}
            title="Unsaved changes"
            descriptionText="All unsaved changes will be lost."
          />),
        }));
    } else {
      onConfirmCancel();
    }
  }, [isDirty, onConfirmCancel, dispatch]);

  return onCancel;
};

export enum FORM_VARIANT {
  STANDALONE,
  DEPENDENT
}

interface Props {
  onConfirmCancel: () => void;
  variant?: FORM_VARIANT;
}

const CustomFormRecordFooter = (props: Props) => {
  const { onConfirmCancel, variant = FORM_VARIANT.STANDALONE } = props;
  const { isOnline } = useAppSettings();
  const { isReadOnly, isSaving, recordORM, onSave } = useCustomFormRecordProvider();
  const { connectionStatus, isAutomaticLoginEnabled } = useCRMStatus();
  const isConnectedToCRM = connectionStatus === CRMConnectionStatus.CONNECTED;
  const onCancel = useCancelCustomForm(onConfirmCancel);
  const canOpenInCRM = variant === FORM_VARIANT.STANDALONE && recordORM?.model.crmFields?.externalId;
  const canSubmitToCRM = variant === FORM_VARIANT.STANDALONE &&
    !isReadOnly &&
    (isConnectedToCRM || isAutomaticLoginEnabled);

  return (
    <DNABox appearance="col">
      <DNADivider />
      <DNABox spacing="sm" alignX="end" alignY="center" style={styles.content}>
        {/* Cancel Button */}
        <Iffy is={!isReadOnly}>
          <DNAButton
            testID="form-cancel-button"
            appearance="outline"
            status="tertiary"
            onPress={onCancel}
            disabled={isSaving}
            children="Cancel"
          />
        </Iffy>
        {/* Save Button */}
        <Iffy is={variant === FORM_VARIANT.DEPENDENT && !isReadOnly}>
          <DNAButton
            testID="form-save-button"
            onPress={() => onSave()}
            disabled={isSaving}
            children="Save"
          />
        </Iffy>
        {/* Submit Button */}
        <Iffy is={canSubmitToCRM}>
          <DNAPopover disablePopover={isOnline}>
            <DNAPopover.Anchor>
              <DNAButton
                testID="form-submit-button"
                onPress={() => onSave(true)}
                disabled={isSaving || !isOnline}
                children="Submit"
              />
            </DNAPopover.Anchor>
            <DNAPopover.Content>
              <DNAText colorMode="dark">{GLOBAL_TENANT_FORM_ERROR.submit_offline}</DNAText>
            </DNAPopover.Content>
          </DNAPopover>
        </Iffy>
        {/* Open in CRM Button */}
        {
          canOpenInCRM &&
            <OpenInCRMButton recordORM={recordORM} />
        }
      </DNABox>
    </DNABox>
  );
};

interface OpenInCRMProps {
  recordORM: CustomFormRecordORM;
}

function OpenInCRMButton(props: OpenInCRMProps) {
  const { recordORM } = props;
  const tenant = useUserTenant();

  const openInCRM = (): void => {
    const isVaultCRM = tenant?.config.crmIntegration?.crmIntegrationType === CrmIntegrationType.VEEVA_VAULT;
    const externalId = recordORM.model.crmFields?.externalId;
    const instanceUrl = tenant?.config.crmIntegration?.instanceUrl;
    if (externalId && instanceUrl) {
      if (isVaultCRM) {
        const object = recordORM.model.crmFields?.apiName;
        if (object) {
          window.open(`${instanceUrl}/ui/#/object/${object}/${externalId}`, '_blank');
        }
      } else {
        window.open(`${instanceUrl}/${externalId}`, '_blank');
      }
    }
  };

  return (
    <DNAButton
      testID="open-in-crm-button"
      appearance="outline"
      status="tertiary"
      onPress={openInCRM}
      children="View in CRM"
    />
  );
}

export default CustomFormRecordFooter;
